import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import ManualesPage from "./ManualesPage";
import FlujogramasPage from "./FlujogramasPage";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  BookOpen, 
  Building2, 
  Calendar,
  CreditCard, 
  Percent, 
  FileText, 
  DollarSign,
  Briefcase,
  Settings,
  Plus,
  Pencil,
  Trash2,
  Search,
  RefreshCw,
  ArrowLeft,
  Calculator,
  Download,
  FileSpreadsheet,
  FileJson,
  FileType,
  File,
  Image as ImageIcon,
  X,
  Landmark,
  Car,
  Megaphone,
  ShoppingBag,
  FileStack,
  Coins,
  Truck,
  Cross,
  AlertTriangle,
  Loader2,
  GitBranch,
  Shield
} from "lucide-react";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';

const submoduloConfig = {
  bancos: { title: "Bancos", icon: Building2, color: "blue" },
  cuentas: { title: "Cuentas Bancarias", icon: CreditCard, color: "green" },
  impuestos: { title: "Tipos de Impuestos", icon: Percent, color: "red" },
  presupuesto: { title: "Códigos Presupuestarios", icon: FileText, color: "orange" },
  oficinas: { title: "Oficinas", icon: Building2, color: "teal" },
  "tipos-calculo": { title: "Tipos de Cálculo", icon: Calculator, color: "violet" },
  cotizaciones: { title: "Cotización de Moneda", icon: DollarSign, color: "yellow" },
  actividades: { title: "Actividades Económicas", icon: Briefcase, color: "indigo" },
  "configuracion-general": { title: "Configuración General", icon: Settings, color: "slate" },
  manuales: { title: "Manuales de Usuario", icon: BookOpen, color: "indigo" },
  flujogramas: { title: "Flujogramas", icon: GitBranch, color: "violet" }
};


const MODULOS_ACCIONES = [
  { key: "catastro", nombre: "Catastro", icon: Landmark, color: "blue", btnFecha: "Actualizar Fecha de Cálculo", btnMoneda: "Actualizar Valor Moneda" },
  { key: "desarrollo-urbano", nombre: "Desarrollo Urbano", icon: Building2, color: "violet", btnFecha: "Actualizar Fecha de Cálculo", btnMoneda: "Actualizar Valor Moneda" },
  { key: "publicidad", nombre: "Publicidad", icon: Megaphone, color: "orange", btnFecha: "Actualizar Fecha de Cálculo", btnMoneda: "Actualizar Valor Moneda" },
  { key: "vehiculos", nombre: "Vehículos", icon: Car, color: "emerald", btnFecha: "Actualizar Fecha de Cálculo", btnMoneda: "Actualizar Valor Moneda" },
  { key: "ambulantes", nombre: "Vendedores Ambulantes", icon: ShoppingBag, color: "teal", btnFecha: "Actualizar Fecha de Cálculo", btnMoneda: "Actualizar Valor Moneda" },
  { key: "liquidaciones", nombre: "Liquidaciones", icon: FileStack, color: "indigo", btnFecha: "Actualizar Fecha de Declaración", btnMoneda: "Actualizar Valor Moneda" },
  { key: "cementerio", nombre: "Cementerio Municipal", icon: Cross, color: "slate", placeholder: true },
  { key: "aseo-urbano", nombre: "Aseo Urbano", icon: Truck, color: "lime", placeholder: true },
];

const AccionesMasivasPanel = ({ API, headers }) => {
  const [resumen, setResumen] = useState(null);
  const [ejecutando, setEjecutando] = useState({});
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(new Date().toISOString().split("T")[0]);

  useEffect(() => { fetchResumen(); }, []);

  const fetchResumen = async () => {
    try {
      const response = await axios.get(`${API}/definiciones/acciones-masivas/resumen`, { headers });
      setResumen(response.data);
    } catch { /* ignore */ }
  };

  const ejecutarAccion = async (modulo, accion) => {
    const loadingKey = `${modulo}-${accion}`;
    setEjecutando(prev => ({ ...prev, [loadingKey]: true }));
    try {
      const url = accion === "actualizar-fecha"
        ? `${API}/definiciones/acciones-masivas/${modulo}/${accion}?fecha=${fechaSeleccionada}`
        : `${API}/definiciones/acciones-masivas/${modulo}/${accion}`;
      const response = await axios.post(url, {}, { headers });
      const d = response.data;
      toast.success(`${d.message}: ${d.registros_actualizados} registros actualizados`);
      fetchResumen();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al ejecutar acción");
    }
    setEjecutando(prev => ({ ...prev, [loadingKey]: false }));
    setConfirmDialog(null);
  };

  const uc = resumen?.cotizacion_activa;

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-orange-600" />
          Acciones Masivas por Módulo
        </h2>
        {uc && uc.valor_unidad_cuenta > 0 && (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-300 text-xs px-3 py-1">
            UC: Bs. {uc.valor_unidad_cuenta?.toLocaleString("es-VE", { minimumFractionDigits: 2 })} | USD: {uc.dolar?.toLocaleString("es-VE", { minimumFractionDigits: 2 })} | EUR: {uc.euro?.toLocaleString("es-VE", { minimumFractionDigits: 2 })}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {MODULOS_ACCIONES.map(mod => {
          const ModIcon = mod.icon;
          const sinPagar = resumen?.modulos?.[mod.key]?.sin_pagar;
          const isPlaceholder = mod.placeholder;
          const colorMap = {
            blue: { bg: "bg-blue-50", border: "border-blue-200", iconColor: "text-blue-600", headerBg: "bg-blue-100" },
            violet: { bg: "bg-violet-50", border: "border-violet-200", iconColor: "text-violet-600", headerBg: "bg-violet-100" },
            orange: { bg: "bg-orange-50", border: "border-orange-200", iconColor: "text-orange-600", headerBg: "bg-orange-100" },
            emerald: { bg: "bg-emerald-50", border: "border-emerald-200", iconColor: "text-emerald-600", headerBg: "bg-emerald-100" },
            teal: { bg: "bg-teal-50", border: "border-teal-200", iconColor: "text-teal-600", headerBg: "bg-teal-100" },
            indigo: { bg: "bg-indigo-50", border: "border-indigo-200", iconColor: "text-indigo-600", headerBg: "bg-indigo-100" },
            slate: { bg: "bg-slate-50", border: "border-slate-200", iconColor: "text-slate-500", headerBg: "bg-slate-100" },
            lime: { bg: "bg-lime-50", border: "border-lime-200", iconColor: "text-lime-600", headerBg: "bg-lime-100" },
          };
          const c = colorMap[mod.color] || colorMap.slate;

          return (
            <Card key={mod.key} className={`${c.border} ${isPlaceholder ? "opacity-60" : ""}`} data-testid={`acciones-modulo-${mod.key}`}>
              <CardContent className="p-0">
                <div className={`flex items-center gap-3 px-4 py-3 ${c.headerBg} rounded-t-lg`}>
                  <ModIcon className={`w-5 h-5 ${c.iconColor}`} />
                  <span className="font-semibold text-sm text-slate-800">{mod.nombre}</span>
                  {!isPlaceholder && sinPagar !== undefined && (
                    <Badge variant="outline" className="ml-auto bg-white text-xs">{sinPagar} sin pagar</Badge>
                  )}
                  {isPlaceholder && (
                    <Badge variant="outline" className="ml-auto bg-amber-50 text-amber-600 border-amber-200 text-xs">En desarrollo</Badge>
                  )}
                </div>
                <div className="p-4 flex gap-3">
                  <Button
                    variant="outline"
                    className={`flex-1 h-auto py-3 flex flex-col items-center gap-1.5 ${c.border} hover:${c.bg}`}
                    disabled={isPlaceholder || ejecutando[`${mod.key}-actualizar-fecha`]}
                    onClick={() => setConfirmDialog({ modulo: mod.key, accion: "actualizar-fecha", nombre: mod.nombre, label: mod.btnFecha })}
                    data-testid={`btn-fecha-${mod.key}`}
                  >
                    {ejecutando[`${mod.key}-actualizar-fecha`] ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : <Calendar className={`w-5 h-5 ${c.iconColor}`} />}
                    <span className="text-xs font-medium text-slate-700 text-center leading-tight">{mod.btnFecha}</span>
                  </Button>
                  <Button
                    variant="outline"
                    className={`flex-1 h-auto py-3 flex flex-col items-center gap-1.5 ${c.border} hover:${c.bg}`}
                    disabled={isPlaceholder || ejecutando[`${mod.key}-actualizar-valor-moneda`]}
                    onClick={() => setConfirmDialog({ modulo: mod.key, accion: "actualizar-valor-moneda", nombre: mod.nombre, label: mod.btnMoneda })}
                    data-testid={`btn-moneda-${mod.key}`}
                  >
                    {ejecutando[`${mod.key}-actualizar-valor-moneda`] ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : <Coins className={`w-5 h-5 ${c.iconColor}`} />}
                    <span className="text-xs font-medium text-slate-700 text-center leading-tight">{mod.btnMoneda}</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Diálogo de confirmación */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-confirmar-accion">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Confirmar Acción Masiva
            </DialogTitle>
          </DialogHeader>
          {confirmDialog && (
            <div className="space-y-3 py-2">
              <p className="text-sm text-slate-600">
                Está a punto de ejecutar <strong>{confirmDialog.label}</strong> en el módulo <strong>{confirmDialog.nombre}</strong>.
              </p>
              <p className="text-sm text-slate-600">
                Esta acción afectará <strong>todos los registros con estatus de pago "Sin pagar" y estatus "Registrado"</strong>.
              </p>
              {confirmDialog.accion === "actualizar-fecha" && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Seleccione la fecha a aplicar:</Label>
                  <Input
                    type="date"
                    value={fechaSeleccionada}
                    onChange={(e) => setFechaSeleccionada(e.target.value)}
                    className="w-full"
                    data-testid="input-fecha-masiva"
                  />
                </div>
              )}
              {confirmDialog.accion === "actualizar-valor-moneda" && uc && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800">
                  Unidad de Cuenta activa: <strong>Bs. {uc.valor_unidad_cuenta?.toLocaleString("es-VE", { minimumFractionDigits: 2 })}</strong>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancelar</Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700"
                  disabled={ejecutando[`${confirmDialog.modulo}-${confirmDialog.accion}`]}
                  onClick={() => ejecutarAccion(confirmDialog.modulo, confirmDialog.accion)}
                  data-testid="btn-confirmar-accion-masiva"
                >
                  {ejecutando[`${confirmDialog.modulo}-${confirmDialog.accion}`] ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ejecutando...</>
                  ) : "Confirmar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


const DefinicionesPage = () => {
  const { submodulo } = useParams();
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [codigosPresupuestarios, setCodigosPresupuestarios] = useState([]);
  const [tiposCalculo, setTiposCalculo] = useState([]);
  const [oficinas, setOficinas] = useState([]);

  // Filtros por columna para Tipos de Impuestos
  const [impuestosFilters, setImpuestosFilters] = useState({
    nombre: "",
    tasa: "",
    tipo_calculo: "",
    codigo_presupuestario: "",
    oficina: "",
    extranet: "",
    descuento: "",
    estado: ""
  });

  // Filtros por columna para Actividades Económicas
  const [actividadesFilters, setActividadesFilters] = useState({
    codigo: "",
    nombre: "",
    codigo_presupuestario: "",
    sector: "",
    ramo: "",
    alicuota: "",
    minimo: ""
  });

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [confirmCotizacionDialog, setConfirmCotizacionDialog] = useState(false);
  
  // Configuration form states
  const [isEditingConfig, setIsEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({});

  const headers = { Authorization: `Bearer ${token}` };
  const currentConfig = submoduloConfig[submodulo] || null;

  // Fetch data based on submodulo
  const fetchData = async () => {
    if (!submodulo || submodulo === "manuales" || submodulo === "flujogramas") return;
    setLoading(true);
    try {
      const endpoint = getEndpoint(submodulo);
      const response = await axios.get(`${API}/definiciones/${endpoint}`, { headers });
      setData(response.data);
      
      // Also fetch bancos if we're in cuentas
      if (submodulo === "cuentas") {
        const bancosResp = await axios.get(`${API}/definiciones/bancos`, { headers });
        setBancos(bancosResp.data);
      }
      
      // Fetch related data for tipos de impuestos
      if (submodulo === "impuestos") {
        const [codigosResp, calculosResp, oficinasResp] = await Promise.all([
          axios.get(`${API}/definiciones/codigos-presupuestarios`, { headers }),
          axios.get(`${API}/definiciones/tipos-calculo`, { headers }),
          axios.get(`${API}/definiciones/oficinas`, { headers })
        ]);
        setCodigosPresupuestarios(codigosResp.data);
        setTiposCalculo(calculosResp.data);
        setOficinas(oficinasResp.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar los datos");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [submodulo]);

  // CRUD operations
  const handleCreate = async () => {
    // Para cotizaciones, verificar si existe un registro activo y mostrar confirmación
    if (submodulo === "cotizaciones" && formData.estatus === "Activo") {
      const cotizacionesActivas = data.filter(c => c.estatus === "Activo");
      console.log("Verificando cotizaciones activas:", cotizacionesActivas.length, "confirmDialog:", confirmCotizacionDialog);
      if (cotizacionesActivas.length > 0 && !confirmCotizacionDialog) {
        setConfirmCotizacionDialog(true);
        return;
      }
    }
    
    try {
      const endpoint = getEndpoint(submodulo);
      
      // Si es cotización activa, primero desactivar TODAS las anteriores
      if (submodulo === "cotizaciones" && formData.estatus === "Activo") {
        const cotizacionesActivas = data.filter(c => c.estatus === "Activo");
        for (const cotizacion of cotizacionesActivas) {
          console.log("Desactivando cotización:", cotizacion.id);
          await axios.put(`${API}/definiciones/${endpoint}/${cotizacion.id}`, 
            { estatus: "Inactivo" }, 
            { headers }
          );
        }
      }
      
      await axios.post(`${API}/definiciones/${endpoint}`, formData, { headers });
      toast.success("Registro creado exitosamente");
      setDialogOpen(false);
      setFormData({});
      setConfirmCotizacionDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear registro");
    }
  };
  
  // Confirmar creación de cotización (desactivar la anterior)
  const handleConfirmCotizacion = async () => {
    await handleCreate();
  };
  
  // Cancelar confirmación de cotización
  const handleCancelCotizacion = () => {
    setConfirmCotizacionDialog(false);
  };

  const handleUpdate = async () => {
    // Para cotizaciones, verificar si se está cambiando a Activo y ya existe uno activo
    if (submodulo === "cotizaciones" && formData.estatus === "Activo") {
      const cotizacionesActivas = data.filter(c => c.estatus === "Activo" && c.id !== editingItem.id);
      if (cotizacionesActivas.length > 0 && !confirmCotizacionDialog) {
        setConfirmCotizacionDialog(true);
        return;
      }
    }
    
    try {
      const endpoint = getEndpoint(submodulo);
      
      // Si es cotización y se está activando, desactivar TODAS las anteriores
      if (submodulo === "cotizaciones" && formData.estatus === "Activo") {
        const cotizacionesActivas = data.filter(c => c.estatus === "Activo" && c.id !== editingItem.id);
        for (const cotizacion of cotizacionesActivas) {
          await axios.put(`${API}/definiciones/${endpoint}/${cotizacion.id}`, 
            { estatus: "Inactivo" }, 
            { headers }
          );
        }
      }
      
      await axios.put(`${API}/definiciones/${endpoint}/${editingItem.id}`, formData, { headers });
      toast.success("Registro actualizado exitosamente");
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      setConfirmCotizacionDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar registro");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este registro?")) return;
    try {
      const endpoint = getEndpoint(submodulo);
      await axios.delete(`${API}/definiciones/${endpoint}/${id}`, { headers });
      toast.success("Registro eliminado exitosamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar registro");
    }
  };

  const getEndpoint = (sub) => {
    const endpoints = {
      bancos: "bancos",
      cuentas: "cuentas-bancarias",
      impuestos: "tipos-impuesto",
      presupuesto: "codigos-presupuestarios",
      oficinas: "oficinas",
      "tipos-calculo": "tipos-calculo",
      cotizaciones: "cotizaciones",
      actividades: "actividades-economicas",
      "configuracion-general": "configuracion"
    };
    return endpoints[sub];
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    // Establecer valores por defecto según el submódulo
    if (submodulo === "cotizaciones") {
      setFormData({ estatus: "Activo" });
    } else {
      setFormData({});
    }
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData(item);
    setDialogOpen(true);
  };

  // Filter data
  const filteredData = data.filter(item => {
    // Filtro de búsqueda general
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        item.codigo?.toLowerCase().includes(searchLower) ||
        item.nombre?.toLowerCase().includes(searchLower) ||
        item.clave?.toLowerCase().includes(searchLower) ||
        item.descripcion?.toLowerCase().includes(searchLower) ||
        item.numero_cuenta?.toLowerCase().includes(searchLower) ||
        item.banco_nombre?.toLowerCase().includes(searchLower) ||
        item.codigo_presupuestario?.toLowerCase().includes(searchLower) ||
        item.nombre_partida?.toLowerCase().includes(searchLower) ||
        item.sector?.toLowerCase().includes(searchLower) ||
        item.ramo?.toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }
    
    // Filtros de columna para impuestos
    if (submodulo === "impuestos") {
      // Filtro por nombre
      if (impuestosFilters.nombre && !item.nombre?.toLowerCase().includes(impuestosFilters.nombre.toLowerCase())) {
        return false;
      }
      // Filtro por tasa
      if (impuestosFilters.tasa && !String(item.tasa || "").includes(impuestosFilters.tasa)) {
        return false;
      }
      // Filtro por tipo de cálculo
      if (impuestosFilters.tipo_calculo) {
        const tipoCalculo = tiposCalculo.find(t => t.id === item.tipo_calculo_id);
        if (!tipoCalculo || !tipoCalculo.nombre.toLowerCase().includes(impuestosFilters.tipo_calculo.toLowerCase())) {
          return false;
        }
      }
      // Filtro por código presupuestario
      if (impuestosFilters.codigo_presupuestario) {
        const codigo = codigosPresupuestarios.find(cp => cp.id === item.codigo_presupuestario_id);
        if (!codigo || !codigo.codigo_presupuestario?.toLowerCase().includes(impuestosFilters.codigo_presupuestario.toLowerCase())) {
          return false;
        }
      }
      // Filtro por oficina
      if (impuestosFilters.oficina) {
        const oficina = oficinas.find(o => o.id === item.oficina_id);
        if (!oficina || !oficina.nombre.toLowerCase().includes(impuestosFilters.oficina.toLowerCase())) {
          return false;
        }
      }
      // Filtro por extranet
      if (impuestosFilters.extranet) {
        const extranetValue = item.extranet ? "si" : "no";
        if (!extranetValue.includes(impuestosFilters.extranet.toLowerCase())) {
          return false;
        }
      }
      // Filtro por descuento
      if (impuestosFilters.descuento) {
        const descuentoValue = item.descuento ? "si" : "no";
        if (!descuentoValue.includes(impuestosFilters.descuento.toLowerCase())) {
          return false;
        }
      }
      // Filtro por estado
      if (impuestosFilters.estado) {
        const estadoValue = item.is_active ? "activo" : "inactivo";
        if (!estadoValue.includes(impuestosFilters.estado.toLowerCase())) {
          return false;
        }
      }
    }
    
    // Filtros de columna para actividades económicas
    if (submodulo === "actividades") {
      if (actividadesFilters.codigo && !item.codigo?.toLowerCase().includes(actividadesFilters.codigo.toLowerCase())) return false;
      if (actividadesFilters.nombre && !item.nombre?.toLowerCase().includes(actividadesFilters.nombre.toLowerCase())) return false;
      if (actividadesFilters.codigo_presupuestario && !(item.codigo_presupuestario || "301020700").toLowerCase().includes(actividadesFilters.codigo_presupuestario.toLowerCase())) return false;
      if (actividadesFilters.sector && !(item.sector || "").toLowerCase().includes(actividadesFilters.sector.toLowerCase())) return false;
      if (actividadesFilters.ramo && !(item.ramo || "").toLowerCase().includes(actividadesFilters.ramo.toLowerCase())) return false;
      if (actividadesFilters.alicuota && !String(item.alicuota || "").includes(actividadesFilters.alicuota)) return false;
      if (actividadesFilters.minimo && !String(item.minimo_tributable || "").includes(actividadesFilters.minimo)) return false;
    }

    return true;
  });

  // Export functions
  const getExportFileName = () => {
    const fileNames = {
      "bancos": "bancos",
      "cuentas": "cuentas_bancarias",
      "impuestos": "tipos_impuestos",
      "presupuesto": "codigos_presupuestarios",
      "oficinas": "oficinas",
      "tipos-calculo": "tipos_calculo",
      "cotizaciones": "cotizacion_moneda",
      "actividades": "actividades_economicas",
      "configuracion-general": "configuracion"
    };
    return fileNames[submodulo] || submodulo;
  };

  const getExportTitle = () => {
    return currentConfig?.title || "Datos";
  };

  const getExportData = () => {
    switch (submodulo) {
      case "bancos":
        return filteredData.map(item => ({
          "Código": item.codigo || "",
          "Nombre del Banco": item.nombre || "",
          "Estado": item.is_active !== false ? "Activo" : "Inactivo"
        }));
      case "cuentas":
        return filteredData.map(item => ({
          "Descripción": item.descripcion || "",
          "Número de Cuenta": item.numero_cuenta || "",
          "Banco": item.banco_nombre || "",
          "Estado": item.is_active !== false ? "Activo" : "Inactivo"
        }));
      case "impuestos":
        return filteredData.map(item => {
          const tipoCalculo = tiposCalculo.find(t => t.id === item.tipo_calculo_id);
          const codigoPresupuestario = codigosPresupuestarios.find(cp => cp.id === item.codigo_presupuestario_id);
          const oficina = oficinas.find(o => o.id === item.oficina_id);
          return {
            "Nombre": item.nombre || "",
            "Código Presupuestario": codigoPresupuestario ? `${codigoPresupuestario.codigo_presupuestario} - ${codigoPresupuestario.nombre_partida}` : "",
            "Tipo de Cálculo": tipoCalculo?.nombre || "",
            "Oficina": oficina?.nombre || "",
            "TCMMV": item.tcmmv ? "Sí" : "No",
            "Extranet": item.extranet ? "Sí" : "No",
            "Descuento": item.descuento ? "Sí" : "No",
            "% Descuento": item.porcentaje_descuento || "0",
            "Estado": item.is_active !== false ? "Activo" : "Inactivo"
          };
        });
      case "presupuesto":
        return filteredData.map(item => ({
          "Código Presupuestario": item.codigo_presupuestario || "",
          "Nombre de la Partida": item.nombre_partida || "",
          "Estado": item.is_active !== false ? "Activo" : "Inactivo"
        }));
      case "oficinas":
        return filteredData.map(item => ({
          "Código": item.codigo || "",
          "Nombre": item.nombre || "",
          "Descripción": item.descripcion || "",
          "Estado": item.is_active !== false ? "Activo" : "Inactivo"
        }));
      case "tipos-calculo":
        return filteredData.map(item => ({
          "Código": item.codigo || "",
          "Nombre": item.nombre || "",
          "Descripción": item.descripcion || "",
          "Estado": item.is_active !== false ? "Activo" : "Inactivo"
        }));
      case "cotizaciones":
        return filteredData.map(item => ({
          "Euro (Bs.)": item.euro?.toFixed(4) || "0.0000",
          "Dólar (Bs.)": item.dolar?.toFixed(4) || "0.0000",
          "Otro (Bs.)": item.otro?.toFixed(4) || "0.0000",
          "Unidad de Cuenta": item.valor_unidad_cuenta?.toFixed(4) || "0.0000",
          "Estatus": item.estatus || "Activo",
          "Fecha": item.fecha ? new Date(item.fecha).toLocaleDateString('es-VE') : ""
        }));
      case "actividades":
        return filteredData.map(item => ({
          "Código": item.codigo || "",
          "Tipo de Act. Eco.": item.nombre || "",
          "Cód. Presupuestario": item.codigo_presupuestario || "301020700",
          "Alícuota": item.alicuota || "",
          "Mínimo Tributable": item.minimo_tributable || "",
          "Sector": item.sector || "",
          "Ramo": item.ramo || "",
          "Estado": item.is_active !== false ? "Activo" : "Inactivo"
        }));
      case "configuracion-general":
        return filteredData.map(item => ({
          "Estado": item.estado || "",
          "Municipio": item.municipio || "",
          "Nombre Alcaldía": item.nombre_alcaldia || "",
          "RIF Alcaldía": item.rif_alcaldia || "",
          "Alcalde/Alcaldesa": item.nombre_alcalde || "",
          "Dirección Fiscal": item.direccion_fiscal || "",
          "Teléfono Fijo": item.telefono_fijo || "",
          "Teléfono Móvil": item.telefono_movil || "",
          "Superintendencia Tributos": item.nombre_superintendencia_tributos || "",
          "Director Tributos": item.nombre_director_tributos || "",
          "Email Tributos": item.email_tributos || "",
          "Director Catastro": item.nombre_director_catastro || "",
          "Email Catastro": item.email_catastro || "",
          "Director Ingeniería": item.nombre_director_ingenieria || "",
          "Email Ingeniería": item.email_ingenieria || "",
          "Director Cementerio": item.nombre_director_cementerio || "",
          "Email Cementerio": item.email_cementerio || "",
          "Moneda Cálculo": item.moneda_calculo || "Bolivares",
          "Correlativo Pago": item.correlativo_comprobantes_pago || 1,
          "Correlativo LAE": item.correlativo_lae || 1,
          "Correlativo Liquidación": item.correlativo_planilla_liquidacion || 1,
          "Validación Documentos": item.validacion_carga_documentos ? "Sí" : "No"
        }));
      default:
        return [];
    }
  };

  const exportToCSV = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const headers = Object.keys(exportData[0]).join(",");
    const rows = exportData.map(row => Object.values(row).map(v => `"${v}"`).join(",")).join("\n");
    const csv = `${headers}\n${rows}`;
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("Exportado a CSV exitosamente");
  };

  const exportToJSON = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.json`);
    toast.success("Exportado a JSON exitosamente");
  };

  const exportToExcel = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, getExportTitle().substring(0, 31));
    XLSX.writeFile(wb, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Exportado a Excel exitosamente");
  };

  const exportToPDF = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const doc = new jsPDF('landscape');
    doc.setFontSize(16);
    doc.text(`Listado de ${getExportTitle()}`, 14, 20);
    doc.setFontSize(10);
    doc.text(`Fecha: ${new Date().toLocaleDateString('es-VE')}`, 14, 28);
    
    const headers = Object.keys(exportData[0]);
    const rows = exportData.map(row => Object.values(row));
    
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 35,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [30, 64, 175] }
    });
    
    doc.save(`${getExportFileName()}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("Exportado a PDF exitosamente");
  };

  const exportToHTML = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const headers = Object.keys(exportData[0]);
    const title = getExportTitle();
    let html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Listado de ${title}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    h1 { color: #1e40af; }
    table { border-collapse: collapse; width: 100%; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
    th { background-color: #1e40af; color: white; }
    tr:nth-child(even) { background-color: #f9fafb; }
    tr:hover { background-color: #f3f4f6; }
    .fecha { color: #6b7280; margin-bottom: 20px; }
  </style>
</head>
<body>
  <h1>Listado de ${title}</h1>
  <p class="fecha">Fecha: ${new Date().toLocaleDateString('es-VE')}</p>
  <table>
    <thead>
      <tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr>
    </thead>
    <tbody>
      ${exportData.map(row => `<tr>${Object.values(row).map(v => `<td>${v}</td>`).join("")}</tr>`).join("")}
    </tbody>
  </table>
</body>
</html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.html`);
    toast.success("Exportado a HTML exitosamente");
  };

  const exportToText = () => {
    const exportData = getExportData();
    if (exportData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const headers = Object.keys(exportData[0]);
    const colWidths = headers.map(h => Math.max(h.length, ...exportData.map(row => String(row[h]).length)));
    const title = getExportTitle().toUpperCase();
    
    let text = `LISTADO DE ${title}\n`;
    text += `Fecha: ${new Date().toLocaleDateString('es-VE')}\n`;
    text += "=".repeat(Math.max(colWidths.reduce((a, b) => a + b + 3, 0), 50)) + "\n\n";
    
    text += headers.map((h, i) => h.padEnd(colWidths[i])).join(" | ") + "\n";
    text += colWidths.map(w => "-".repeat(w)).join("-+-") + "\n";
    
    exportData.forEach(row => {
      text += headers.map((h, i) => String(row[h]).padEnd(colWidths[i])).join(" | ") + "\n";
    });
    
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `${getExportFileName()}_${new Date().toISOString().split('T')[0]}.txt`);
    toast.success("Exportado a Texto exitosamente");
  };

  // Render form fields based on submodulo
  const renderFormFields = () => {
    switch (submodulo) {
      case "bancos":
        return (
          <>
            <div className="space-y-2">
              <Label>Código del Banco</Label>
              <Input 
                value={formData.codigo || ""} 
                onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                placeholder="Ej: 0102"
                data-testid="form-codigo"
              />
            </div>
            <div className="space-y-2">
              <Label>Nombre del Banco</Label>
              <Input 
                value={formData.nombre || ""} 
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                placeholder="Ej: Banco de Venezuela"
                data-testid="form-nombre"
              />
            </div>
          </>
        );

      case "cuentas":
        return (
          <>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input 
                value={formData.descripcion || ""} 
                onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                placeholder="Ej: Cuenta principal de recaudación"
                data-testid="form-descripcion"
              />
            </div>
            <div className="space-y-2">
              <Label>Número de Cuenta</Label>
              <Input 
                value={formData.numero_cuenta || ""} 
                onChange={(e) => setFormData({...formData, numero_cuenta: e.target.value})}
                placeholder="Ej: 0102-0000-00-0000000000"
                data-testid="form-numero-cuenta"
              />
            </div>
            <div className="space-y-2">
              <Label>Banco</Label>
              <Select 
                value={formData.banco_id || ""} 
                onValueChange={(value) => setFormData({...formData, banco_id: value})}
              >
                <SelectTrigger data-testid="form-banco">
                  <SelectValue placeholder="Seleccione un banco" />
                </SelectTrigger>
                <SelectContent>
                  {bancos.filter(b => b.is_active !== false).map(banco => (
                    <SelectItem key={banco.id} value={banco.id}>{banco.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case "impuestos":
        return (
          <>
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input 
                value={formData.nombre || ""} 
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                placeholder="Ej: Impuesto sobre Actividades Económicas"
                data-testid="form-nombre"
              />
            </div>
            <div className="space-y-2">
              <Label>Tasa</Label>
              <Input 
                type="number"
                step="0.01"
                value={formData.tasa || ""} 
                onChange={(e) => setFormData({...formData, tasa: parseFloat(e.target.value) || 0})}
                placeholder="Ej: 2.5"
                data-testid="form-tasa"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Cálculo</Label>
              <Select 
                value={formData.tipo_calculo_id || ""} 
                onValueChange={(value) => setFormData({...formData, tipo_calculo_id: value})}
              >
                <SelectTrigger data-testid="form-tipo-calculo">
                  <SelectValue placeholder="Seleccione tipo de cálculo" />
                </SelectTrigger>
                <SelectContent>
                  {tiposCalculo.map(tc => (
                    <SelectItem key={tc.id} value={tc.id}>{tc.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Código Presupuestario</Label>
              <Select 
                value={formData.codigo_presupuestario_id || ""} 
                onValueChange={(value) => setFormData({...formData, codigo_presupuestario_id: value})}
              >
                <SelectTrigger data-testid="form-codigo-presupuestario">
                  <SelectValue placeholder="Seleccione código presupuestario" />
                </SelectTrigger>
                <SelectContent>
                  {codigosPresupuestarios.map(cp => (
                    <SelectItem key={cp.id} value={cp.id}>{cp.codigo_presupuestario || cp.codigo} - {cp.nombre_partida || cp.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Oficina</Label>
              <Select 
                value={formData.oficina_id || ""} 
                onValueChange={(value) => setFormData({...formData, oficina_id: value})}
              >
                <SelectTrigger data-testid="form-oficina">
                  <SelectValue placeholder="Seleccione oficina" />
                </SelectTrigger>
                <SelectContent>
                  {oficinas.map((oficina) => (
                    <SelectItem key={oficina.id} value={oficina.id}>
                      {oficina.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>TCMMV</Label>
              <Input 
                value={formData.tcmmv || ""} 
                onChange={(e) => setFormData({...formData, tcmmv: e.target.value})}
                placeholder="Ej: Valor TCMMV"
                data-testid="form-tcmmv"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Switch 
                  checked={formData.extranet || false}
                  onCheckedChange={(checked) => setFormData({...formData, extranet: checked})}
                  data-testid="form-extranet"
                />
                <Label>Extranet</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch 
                  checked={formData.descuento || false}
                  onCheckedChange={(checked) => setFormData({
                    ...formData, 
                    descuento: checked,
                    // Si se desactiva el descuento, resetear el porcentaje y fechas
                    porcentaje_descuento: checked ? (formData.porcentaje_descuento || 0) : 0,
                    fecha_descuento_desde: checked ? (formData.fecha_descuento_desde || null) : null,
                    fecha_descuento_hasta: checked ? (formData.fecha_descuento_hasta || null) : null
                  })}
                  data-testid="form-descuento"
                />
                <Label>Descuento</Label>
              </div>
            </div>
            {formData.descuento && (
              <>
                <div className="space-y-2">
                  <Label>Porcentaje de Descuento (%)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.porcentaje_descuento || ""} 
                    onChange={(e) => setFormData({...formData, porcentaje_descuento: parseFloat(e.target.value) || 0})}
                    placeholder="Ej: 10"
                    data-testid="form-porcentaje-descuento"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fechas de Descuento</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Desde</Label>
                      <Input 
                        type="date"
                        value={formData.fecha_descuento_desde || ""} 
                        onChange={(e) => setFormData({...formData, fecha_descuento_desde: e.target.value})}
                        data-testid="form-fecha-descuento-desde"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Hasta</Label>
                      <Input 
                        type="date"
                        value={formData.fecha_descuento_hasta || ""} 
                        onChange={(e) => setFormData({...formData, fecha_descuento_hasta: e.target.value})}
                        data-testid="form-fecha-descuento-hasta"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            {/* ============ ANCLAR A LIQUIDACIÓN ============ */}
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.anclar_liquidacion || false}
                  onCheckedChange={(checked) => setFormData({
                    ...formData,
                    anclar_liquidacion: checked,
                    modulos_anclados: checked ? (formData.modulos_anclados || []) : [],
                  })}
                  data-testid="form-anclar-liquidacion"
                />
                <Label className="font-medium">Anclar a liquidación</Label>
              </div>
              <p className="text-xs text-slate-500 mt-1 ml-10">
                Al activarlo, este impuesto se agregará automáticamente en los cálculos de los módulos seleccionados.
              </p>
              {formData.anclar_liquidacion && (
                <div className="mt-3 ml-10 space-y-2" data-testid="anclar-modulos-panel">
                  <Label className="text-sm text-slate-700">Módulos donde aplicar:</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: "__all__", label: "Todos" },
                      { id: "catastro", label: "Boletín de Cálculo" },
                      { id: "desarrollo-urbano", label: "Boletín - Cálculo" },
                      { id: "publicidad", label: "Cálculo de Publicidad" },
                      { id: "vehiculos", label: "Cálculo de Vehículo" },
                      { id: "ambulantes", label: "Cálculo Vendedores Ambulantes" },
                      { id: "licores", label: "Cálculo de Licores" },
                    ].map((opt) => {
                      const allModulos = ["catastro", "desarrollo-urbano", "publicidad", "vehiculos", "ambulantes", "licores"];
                      const current = formData.modulos_anclados || [];
                      const isAll = opt.id === "__all__";
                      const allSelected = allModulos.every((m) => current.includes(m));
                      const checked = isAll ? allSelected : current.includes(opt.id);
                      return (
                        <label
                          key={opt.id}
                          className={`flex items-center gap-2 text-sm cursor-pointer p-2 rounded border hover:bg-slate-50 ${
                            checked ? "bg-blue-50 border-blue-300" : "bg-white border-slate-200"
                          }`}
                          data-testid={`modulo-anclaje-${opt.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              if (isAll) {
                                setFormData({
                                  ...formData,
                                  modulos_anclados: e.target.checked ? [...allModulos] : [],
                                });
                              } else {
                                const next = e.target.checked
                                  ? [...new Set([...current, opt.id])]
                                  : current.filter((x) => x !== opt.id);
                                setFormData({ ...formData, modulos_anclados: next });
                              }
                            }}
                            className="w-4 h-4 accent-blue-600"
                          />
                          <span className={isAll ? "font-semibold text-blue-700" : ""}>{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Descripción (Opcional)</Label>
              <Input 
                value={formData.descripcion || ""} 
                onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
              />
            </div>
          </>
        );

      case "presupuesto":
        return (
          <>
            <div className="space-y-2">
              <Label>Código Presupuestario</Label>
              <Input 
                value={formData.codigo_presupuestario || ""} 
                onChange={(e) => setFormData({...formData, codigo_presupuestario: e.target.value})}
                placeholder="Ej: 1.1.01.01"
                data-testid="form-codigo-presupuestario"
              />
            </div>
            <div className="space-y-2">
              <Label>Nombre de la Partida</Label>
              <Input 
                value={formData.nombre_partida || ""} 
                onChange={(e) => setFormData({...formData, nombre_partida: e.target.value})}
                placeholder="Ej: Ingresos Tributarios"
                data-testid="form-nombre-partida"
              />
            </div>
          </>
        );

      case "oficinas":
        return (
          <>
            <div className="space-y-2">
              <Label>Nombre de la Oficina</Label>
              <Input 
                value={formData.nombre || ""} 
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                placeholder="Ej: Oficina Principal"
                data-testid="form-nombre"
              />
            </div>
          </>
        );

      case "tipos-calculo":
        return (
          <>
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input 
                value={formData.nombre || ""} 
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                placeholder="Ej: Cálculo de Impuesto Inmobiliario"
                data-testid="form-nombre"
              />
            </div>
            <div className="space-y-2">
              <Label>Fórmula</Label>
              <Input 
                value={formData.formula || ""} 
                onChange={(e) => setFormData({...formData, formula: e.target.value})}
                placeholder="Ej: BASE * ALICUOTA / 100"
                data-testid="form-formula"
              />
            </div>
          </>
        );

      case "cotizaciones":
        return (
          <>
            <div className="space-y-2">
              <Label>Euro (Bs.)</Label>
              <Input 
                type="number"
                step="0.0001"
                value={formData.euro || ""} 
                onChange={(e) => setFormData({...formData, euro: parseFloat(e.target.value) || 0})}
                placeholder="Ej: 40.50"
                data-testid="form-euro"
              />
            </div>
            <div className="space-y-2">
              <Label>Dólar (Bs.)</Label>
              <Input 
                type="number"
                step="0.0001"
                value={formData.dolar || ""} 
                onChange={(e) => setFormData({...formData, dolar: parseFloat(e.target.value) || 0})}
                placeholder="Ej: 36.50"
                data-testid="form-dolar"
              />
            </div>
            <div className="space-y-2">
              <Label>Otro (Bs.)</Label>
              <Input 
                type="number"
                step="0.0001"
                value={formData.otro || ""} 
                onChange={(e) => setFormData({...formData, otro: parseFloat(e.target.value) || 0})}
                placeholder="Ej: 0.0085"
                data-testid="form-otro"
              />
            </div>
            <div className="space-y-2">
              <Label>Valor Unidad de Cuenta (Bs.)</Label>
              <Input 
                type="number"
                step="0.0001"
                value={formData.valor_unidad_cuenta || ""} 
                onChange={(e) => setFormData({...formData, valor_unidad_cuenta: parseFloat(e.target.value) || 0})}
                placeholder="Ej: 0.40"
                data-testid="form-valor-unidad"
              />
            </div>
            <div className="space-y-2">
              <Label>Estatus</Label>
              <Select 
                value={formData.estatus || "Activo"} 
                onValueChange={(value) => setFormData({...formData, estatus: value})}
              >
                <SelectTrigger data-testid="form-estatus">
                  <SelectValue placeholder="Seleccione estatus" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Activo">Activo</SelectItem>
                  <SelectItem value="Inactivo">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case "actividades":
        return (
          <>
            <div className="space-y-2">
              <Label>Código</Label>
              <Input 
                value={formData.codigo || ""} 
                onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                placeholder="Ej: ACT-001"
                data-testid="form-codigo"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Act. Eco.</Label>
              <Input 
                value={formData.nombre || ""} 
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                placeholder="Ej: Venta al por mayor de alimentos"
                data-testid="form-nombre"
              />
            </div>
            <div className="space-y-2">
              <Label>Sector</Label>
              <Select 
                value={formData.sector || "terciario"} 
                onValueChange={(value) => setFormData({...formData, sector: value})}
              >
                <SelectTrigger data-testid="form-sector">
                  <SelectValue placeholder="Seleccione sector" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="primario">Primario</SelectItem>
                  <SelectItem value="secundario">Secundario</SelectItem>
                  <SelectItem value="terciario">Terciario</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ramo</Label>
              <Select 
                value={formData.ramo || "ACTIVIDAD NO ESPECIFICADAS"} 
                onValueChange={(value) => setFormData({...formData, ramo: value})}
              >
                <SelectTrigger data-testid="form-ramo">
                  <SelectValue placeholder="Seleccione ramo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVIDAD NO ESPECIFICADAS">Actividad No Especificadas</SelectItem>
                  <SelectItem value="COMERCIO AL DETAL">Comercio al Detal</SelectItem>
                  <SelectItem value="COMERCIO AL POR MAYOR">Comercio al Por Mayor</SelectItem>
                  <SelectItem value="CONSTRUCCIÓN">Construcción</SelectItem>
                  <SelectItem value="CONSTRUCCIÓN Y SERVICIOS A LA INDUSTRIA PETROLERA">Construcción y Servicios a la Industria Petrolera, Petroquímica y Similares</SelectItem>
                  <SelectItem value="EMPRENDEDOR">Emprendedor</SelectItem>
                  <SelectItem value="MANUFACTURA">Manufactura</SelectItem>
                  <SelectItem value="SERVICIOS PERSONALES Y NO PERSONALES">Servicios Personales y No Personales</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Alícuota (%)</Label>
              <Input 
                type="number"
                step="0.01"
                value={formData.alicuota || ""} 
                onChange={(e) => setFormData({...formData, alicuota: parseFloat(e.target.value)})}
                placeholder="Ej: 1.5"
                data-testid="form-alicuota"
              />
            </div>
            <div className="space-y-2">
              <Label>Mínimo Tributable</Label>
              <Input 
                type="number"
                step="0.01"
                min="0"
                value={formData.minimo_tributable ?? ""} 
                onChange={(e) => setFormData({...formData, minimo_tributable: e.target.value === "" ? null : parseFloat(e.target.value)})}
                placeholder="Ej: 100.00"
                data-testid="form-minimo"
              />
            </div>
            <div className="space-y-2">
              <Label>Código Presupuestario</Label>
              <Input 
                value={formData.codigo_presupuestario || "301020700"} 
                onChange={(e) => setFormData({...formData, codigo_presupuestario: e.target.value})}
                placeholder="301020700"
                data-testid="form-codigo-presupuestario"
              />
              <p className="text-xs text-slate-500">Predeterminado: 301020700 - ACT. ECONÓMICAS, INDUSTRIA, COMERCIO Y SERVICIO</p>
            </div>
            <div className="space-y-2">
              <Label>Descripción (Opcional)</Label>
              <Input 
                value={formData.descripcion || ""} 
                onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
              />
            </div>
          </>
        );

      case "configuracion-general":
        return (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            {/* Datos de la Alcaldía */}
            <div className="border-b pb-3">
              <h4 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Datos de la Alcaldía
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Input 
                    value={formData.estado || ""} 
                    onChange={(e) => setFormData({...formData, estado: e.target.value})}
                    placeholder="Ej: Miranda"
                    data-testid="form-estado"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Municipio</Label>
                  <Input 
                    value={formData.municipio || ""} 
                    onChange={(e) => setFormData({...formData, municipio: e.target.value})}
                    placeholder="Ej: Zamora"
                    data-testid="form-municipio"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label>Nombre de la Alcaldía</Label>
                <Input 
                  value={formData.nombre_alcaldia || ""} 
                  onChange={(e) => setFormData({...formData, nombre_alcaldia: e.target.value})}
                  placeholder="Ej: Alcaldía del Municipio Zamora"
                  data-testid="form-nombre-alcaldia"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-2">
                  <Label>RIF Alcaldía</Label>
                  <Input 
                    value={formData.rif_alcaldia || ""} 
                    onChange={(e) => setFormData({...formData, rif_alcaldia: e.target.value})}
                    placeholder="Ej: G-20000001-0"
                    data-testid="form-rif-alcaldia"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre del Alcalde/Alcaldesa</Label>
                  <Input 
                    value={formData.nombre_alcalde || ""} 
                    onChange={(e) => setFormData({...formData, nombre_alcalde: e.target.value})}
                    placeholder="Nombre completo"
                    data-testid="form-nombre-alcalde"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label>Dirección Fiscal</Label>
                <Input 
                  value={formData.direccion_fiscal || ""} 
                  onChange={(e) => setFormData({...formData, direccion_fiscal: e.target.value})}
                  placeholder="Dirección completa"
                  data-testid="form-direccion-fiscal"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-2">
                  <Label>Teléfono Fijo</Label>
                  <Input 
                    value={formData.telefono_fijo || ""} 
                    onChange={(e) => setFormData({...formData, telefono_fijo: e.target.value})}
                    placeholder="Ej: 0212-1234567"
                    data-testid="form-telefono-fijo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono Móvil</Label>
                  <Input 
                    value={formData.telefono_movil || ""} 
                    onChange={(e) => setFormData({...formData, telefono_movil: e.target.value})}
                    placeholder="Ej: 0414-1234567"
                    data-testid="form-telefono-movil"
                  />
                </div>
              </div>
            </div>

            {/* Dirección de Tributos */}
            <div className="border-b pb-3">
              <h4 className="text-sm font-semibold text-green-700 mb-3">Dirección de Tributos</h4>
              <div className="space-y-2">
                <Label>Nombre Superintendencia o Dirección de Tributos</Label>
                <Input 
                  value={formData.nombre_superintendencia_tributos || ""} 
                  onChange={(e) => setFormData({...formData, nombre_superintendencia_tributos: e.target.value})}
                  placeholder="Ej: Superintendencia Municipal de Administración Tributaria"
                  data-testid="form-superintendencia"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-2">
                  <Label>Nombre Director(a) de Tributos</Label>
                  <Input 
                    value={formData.nombre_director_tributos || ""} 
                    onChange={(e) => setFormData({...formData, nombre_director_tributos: e.target.value})}
                    placeholder="Nombre completo"
                    data-testid="form-director-tributos"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Tributos</Label>
                  <Input 
                    type="email"
                    value={formData.email_tributos || ""} 
                    onChange={(e) => setFormData({...formData, email_tributos: e.target.value})}
                    placeholder="tributos@alcaldia.gob.ve"
                    data-testid="form-email-tributos"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label>Firma Director(a) de Tributos (URL imagen)</Label>
                <Input 
                  value={formData.firma_director_tributos || ""} 
                  onChange={(e) => setFormData({...formData, firma_director_tributos: e.target.value})}
                  placeholder="URL de la imagen de firma"
                  data-testid="form-firma-tributos"
                />
              </div>
            </div>

            {/* Catastro */}
            <div className="border-b pb-3">
              <h4 className="text-sm font-semibold text-yellow-700 mb-3">Catastro</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Nombre Director(a) de Catastro</Label>
                  <Input 
                    value={formData.nombre_director_catastro || ""} 
                    onChange={(e) => setFormData({...formData, nombre_director_catastro: e.target.value})}
                    placeholder="Nombre completo"
                    data-testid="form-director-catastro"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Catastro</Label>
                  <Input 
                    type="email"
                    value={formData.email_catastro || ""} 
                    onChange={(e) => setFormData({...formData, email_catastro: e.target.value})}
                    placeholder="catastro@alcaldia.gob.ve"
                    data-testid="form-email-catastro"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label>Firma Director(a) de Catastro (URL imagen)</Label>
                <Input 
                  value={formData.firma_director_catastro || ""} 
                  onChange={(e) => setFormData({...formData, firma_director_catastro: e.target.value})}
                  placeholder="URL de la imagen de firma"
                  data-testid="form-firma-catastro"
                />
              </div>
            </div>

            {/* Ingeniería Municipal */}
            <div className="border-b pb-3">
              <h4 className="text-sm font-semibold text-purple-700 mb-3">Ingeniería Municipal</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Nombre Director(a) de Ingeniería</Label>
                  <Input 
                    value={formData.nombre_director_ingenieria || ""} 
                    onChange={(e) => setFormData({...formData, nombre_director_ingenieria: e.target.value})}
                    placeholder="Nombre completo"
                    data-testid="form-director-ingenieria"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Ingeniería Municipal</Label>
                  <Input 
                    type="email"
                    value={formData.email_ingenieria || ""} 
                    onChange={(e) => setFormData({...formData, email_ingenieria: e.target.value})}
                    placeholder="ingenieria@alcaldia.gob.ve"
                    data-testid="form-email-ingenieria"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label>Firma Director(a) de Ingeniería (URL imagen)</Label>
                <Input 
                  value={formData.firma_director_ingenieria || ""} 
                  onChange={(e) => setFormData({...formData, firma_director_ingenieria: e.target.value})}
                  placeholder="URL de la imagen de firma"
                  data-testid="form-firma-ingenieria"
                />
              </div>
            </div>

            {/* Cementerio */}
            <div className="border-b pb-3">
              <h4 className="text-sm font-semibold text-stone-700 mb-3">Cementerio Municipal</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Nombre Director(a) del Cementerio</Label>
                  <Input 
                    value={formData.nombre_director_cementerio || ""} 
                    onChange={(e) => setFormData({...formData, nombre_director_cementerio: e.target.value})}
                    placeholder="Nombre completo"
                    data-testid="form-director-cementerio"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Cementerio</Label>
                  <Input 
                    type="email"
                    value={formData.email_cementerio || ""} 
                    onChange={(e) => setFormData({...formData, email_cementerio: e.target.value})}
                    placeholder="cementerio@alcaldia.gob.ve"
                    data-testid="form-email-cementerio"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label>Firma Director(a) del Cementerio (URL imagen)</Label>
                <Input 
                  value={formData.firma_director_cementerio || ""} 
                  onChange={(e) => setFormData({...formData, firma_director_cementerio: e.target.value})}
                  placeholder="URL de la imagen de firma"
                  data-testid="form-firma-cementerio"
                />
              </div>
            </div>

            {/* Aseo Urbano */}
            <div className="border-b pb-3">
              <h4 className="text-sm font-semibold text-lime-700 mb-3">Aseo Urbano</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Director(a) de Aseo Urbano</Label>
                  <Input 
                    value={formData.nombre_director_aseo_urbano || ""} 
                    onChange={(e) => setFormData({...formData, nombre_director_aseo_urbano: e.target.value})}
                    placeholder="Nombre completo"
                    data-testid="form-director-aseo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Aseo Urbano</Label>
                  <Input 
                    type="email"
                    value={formData.email_aseo_urbano || ""} 
                    onChange={(e) => setFormData({...formData, email_aseo_urbano: e.target.value})}
                    placeholder="aseourbano@alcaldia.gob.ve"
                    data-testid="form-email-aseo"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label>Firma Director(a) Aseo Urbano (URL imagen)</Label>
                <Input 
                  value={formData.firma_director_aseo_urbano || ""} 
                  onChange={(e) => setFormData({...formData, firma_director_aseo_urbano: e.target.value})}
                  placeholder="URL de la imagen de firma"
                  data-testid="form-firma-aseo"
                />
              </div>
            </div>

            {/* Parámetros de Declaración */}
            <div className="border-b pb-3">
              <h4 className="text-sm font-semibold text-red-700 mb-3">Parámetros de Declaración</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Fecha Tope Declaración sin Multa</Label>
                  <Input 
                    type="date"
                    value={formData.fecha_tope_sin_multa || ""} 
                    onChange={(e) => setFormData({...formData, fecha_tope_sin_multa: e.target.value})}
                    data-testid="form-fecha-sin-multa"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Tope Declaración con Descuento</Label>
                  <Input 
                    type="date"
                    value={formData.fecha_tope_con_descuento || ""} 
                    onChange={(e) => setFormData({...formData, fecha_tope_con_descuento: e.target.value})}
                    data-testid="form-fecha-con-descuento"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-2">
                  <Label>Valor de la Multa (%)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.valor_multa || ""} 
                    onChange={(e) => setFormData({...formData, valor_multa: parseFloat(e.target.value) || 0})}
                    placeholder="Ej: 10"
                    data-testid="form-valor-multa"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Multa</Label>
                  <Input 
                    type="date"
                    value={formData.fecha_multa || ""} 
                    onChange={(e) => setFormData({...formData, fecha_multa: e.target.value})}
                    data-testid="form-fecha-multa"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-2">
                  <Label>Valor Descuento Act. Económica (%)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.valor_descuento_actividad_economica || ""} 
                    onChange={(e) => setFormData({...formData, valor_descuento_actividad_economica: parseFloat(e.target.value) || 0})}
                    placeholder="Ej: 5"
                    data-testid="form-valor-descuento"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Inicio del Descuento</Label>
                  <Input 
                    type="date"
                    value={formData.fecha_inicio_descuento || ""} 
                    onChange={(e) => setFormData({...formData, fecha_inicio_descuento: e.target.value})}
                    data-testid="form-fecha-inicio-descuento"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label>Moneda de Cálculo</Label>
                <Select 
                  value={formData.moneda_calculo || "Bolivares"} 
                  onValueChange={(value) => setFormData({...formData, moneda_calculo: value})}
                >
                  <SelectTrigger data-testid="form-moneda">
                    <SelectValue placeholder="Seleccione moneda" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bolivares">Bolívares</SelectItem>
                    <SelectItem value="Dolares">Dólares</SelectItem>
                    <SelectItem value="Euros">Euros</SelectItem>
                    <SelectItem value="Petros">Petros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Correlativos */}
            <div className="border-b pb-3">
              <h4 className="text-sm font-semibold text-cyan-700 mb-3">Correlativos</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Comprobantes de Pago</Label>
                  <Input 
                    type="number"
                    value={formData.correlativo_comprobantes_pago || 1} 
                    onChange={(e) => setFormData({...formData, correlativo_comprobantes_pago: parseInt(e.target.value) || 1})}
                    data-testid="form-correlativo-pago"
                  />
                </div>
                <div className="space-y-2">
                  <Label>LAE</Label>
                  <Input 
                    type="number"
                    value={formData.correlativo_lae || 1} 
                    onChange={(e) => setFormData({...formData, correlativo_lae: parseInt(e.target.value) || 1})}
                    data-testid="form-correlativo-lae"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Planilla Liquidación</Label>
                  <Input 
                    type="number"
                    value={formData.correlativo_planilla_liquidacion || 1} 
                    onChange={(e) => setFormData({...formData, correlativo_planilla_liquidacion: parseInt(e.target.value) || 1})}
                    data-testid="form-correlativo-planilla"
                  />
                </div>
              </div>
            </div>

            {/* Validación */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Validación</h4>
              <div className="flex items-center space-x-2">
                <Switch 
                  checked={formData.validacion_carga_documentos || false}
                  onCheckedChange={(checked) => setFormData({...formData, validacion_carga_documentos: checked})}
                  data-testid="form-validacion-documentos"
                />
                <Label>Validación Carga de Documentos</Label>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Get table columns based on submodulo
  const getTableColumns = () => {
    switch (submodulo) {
      case "bancos":
        return [
          { key: "codigo", header: "Código" },
          { key: "nombre", header: "Nombre del Banco" },
          { key: "is_active", header: "Estado", render: (item) => (
            <Badge variant="outline" className={item.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}>
              {item.is_active ? "Activo" : "Inactivo"}
            </Badge>
          )}
        ];
      case "cuentas":
        return [
          { key: "descripcion", header: "Descripción" },
          { key: "numero_cuenta", header: "Número de Cuenta" },
          { key: "banco_nombre", header: "Banco" }
        ];
      case "impuestos":
        return [
          { key: "nombre", header: "Nombre" },
          { key: "tasa", header: "Tasa", render: (item) => (
            <span className="font-mono text-blue-600">{item.tasa}</span>
          )},
          { key: "tipo_calculo_id", header: "Tipo de Cálculo", render: (item) => {
            if (!item.tipo_calculo_id) return <span className="text-slate-400">-</span>;
            const tipoCalculo = tiposCalculo.find(t => t.id === item.tipo_calculo_id);
            return tipoCalculo ? (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {tipoCalculo.nombre}
              </Badge>
            ) : <span className="text-slate-400">-</span>;
          }},
          { key: "codigo_presupuestario_id", header: "Código Presupuestario", render: (item) => {
            if (!item.codigo_presupuestario_id) return <span className="text-slate-400">-</span>;
            const codigo = codigosPresupuestarios.find(cp => cp.id === item.codigo_presupuestario_id);
            return codigo ? (
              <span className="text-sm">{codigo.codigo_presupuestario}</span>
            ) : <span className="text-slate-400">-</span>;
          }},
          { key: "oficina_id", header: "Oficina", render: (item) => {
            if (!item.oficina_id) return <span className="text-slate-400">-</span>;
            const oficina = oficinas.find(o => o.id === item.oficina_id);
            return oficina ? (
              <span className="text-sm">{oficina.nombre}</span>
            ) : <span className="text-slate-400">-</span>;
          }},
          { key: "extranet", header: "Extranet", render: (item) => (
            <Badge variant="outline" className={item.extranet ? "bg-green-50 text-green-700" : "bg-slate-50 text-slate-600"}>
              {item.extranet ? "Sí" : "No"}
            </Badge>
          )},
          { key: "descuento", header: "Descuento", render: (item) => (
            <Badge variant="outline" className={item.descuento ? "bg-yellow-50 text-yellow-700" : "bg-slate-50 text-slate-600"}>
              {item.descuento ? `Sí (${item.porcentaje_descuento || 0}%)` : "No"}
            </Badge>
          )},
          { key: "anclar_liquidacion", header: "Anclado a", render: (item) => {
            if (!item.anclar_liquidacion || !(item.modulos_anclados || []).length) {
              return <span className="text-slate-400 text-xs">No</span>;
            }
            const LABELS = {
              "catastro": "Bol. Cálculo",
              "desarrollo-urbano": "Bol. - Cálculo",
              "publicidad": "Publicidad",
              "vehiculos": "Vehículo",
              "ambulantes": "Ambulantes",
              "licores": "Licores",
            };
            const mods = item.modulos_anclados || [];
            const isAll = mods.length === 6;
            if (isAll) {
              return <Badge className="bg-blue-100 text-blue-700 border-blue-200 border text-[10px]">Todos</Badge>;
            }
            return (
              <div className="flex flex-wrap gap-1 max-w-[220px]">
                {mods.slice(0, 3).map((m) => (
                  <Badge key={m} variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                    {LABELS[m] || m}
                  </Badge>
                ))}
                {mods.length > 3 && (
                  <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600">
                    +{mods.length - 3}
                  </Badge>
                )}
              </div>
            );
          }},
          { key: "is_active", header: "Estado", render: (item) => (
            <Badge variant="outline" className={item.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}>
              {item.is_active ? "Activo" : "Inactivo"}
            </Badge>
          )}
        ];
      case "presupuesto":
        return [
          { key: "codigo_presupuestario", header: "Código Presupuestario" },
          { key: "nombre_partida", header: "Nombre de la Partida" },
          { key: "is_active", header: "Estado", render: (item) => (
            <Badge variant="outline" className={item.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}>
              {item.is_active ? "Activo" : "Inactivo"}
            </Badge>
          )}
        ];
      case "oficinas":
        return [
          { key: "nombre", header: "Nombre de la Oficina" },
          { key: "is_active", header: "Estado", render: (item) => (
            <Badge variant="outline" className={item.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}>
              {item.is_active ? "Activo" : "Inactivo"}
            </Badge>
          )}
        ];
      case "tipos-calculo":
        return [
          { key: "nombre", header: "Nombre" },
          { key: "formula", header: "Fórmula", render: (item) => (
            <code className="bg-slate-100 px-2 py-1 rounded text-sm text-blue-700">{item.formula}</code>
          )},
          { key: "is_active", header: "Estado", render: (item) => (
            <Badge variant="outline" className={item.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}>
              {item.is_active ? "Activo" : "Inactivo"}
            </Badge>
          )}
        ];
      case "cotizaciones":
        return [
          { key: "euro", header: "Euro", render: (item) => (
            <span className="font-mono text-blue-600">Bs. {item.euro?.toFixed(4)}</span>
          )},
          { key: "dolar", header: "Dólar", render: (item) => (
            <span className="font-mono text-green-600">Bs. {item.dolar?.toFixed(4)}</span>
          )},
          { key: "otro", header: "Otro", render: (item) => (
            <span className="font-mono text-orange-600">Bs. {item.otro?.toFixed(4)}</span>
          )},
          { key: "valor_unidad_cuenta", header: "Unidad de Cuenta", render: (item) => (
            <span className="font-mono text-purple-600">Bs. {item.valor_unidad_cuenta?.toFixed(4)}</span>
          )},
          { key: "estatus", header: "Estatus", render: (item) => (
            <Badge variant="outline" className={item.estatus === "Activo" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}>
              {item.estatus || "Activo"}
            </Badge>
          )},
          { key: "fecha", header: "Fecha", render: (item) => (
            <span className="text-slate-500 text-xs">{new Date(item.fecha).toLocaleDateString('es-VE')}</span>
          )}
        ];
      case "actividades":
        return [
          { key: "codigo", header: "Código" },
          { key: "nombre", header: "Tipo de Act. Eco." },
          { key: "codigo_presupuestario", header: "Cód. Presupuestario", render: (item) => (
            <span className="font-mono text-xs text-slate-700">{item.codigo_presupuestario || "301020700"}</span>
          )},
          { key: "sector", header: "Sector", render: (item) => (
            <Badge variant="outline" className={
              item.sector === "primario" ? "bg-green-50 text-green-700 border-green-200" :
              item.sector === "secundario" ? "bg-blue-50 text-blue-700 border-blue-200" :
              "bg-purple-50 text-purple-700 border-purple-200"
            }>
              {item.sector ? item.sector.charAt(0).toUpperCase() + item.sector.slice(1) : "Terciario"}
            </Badge>
          )},
          { key: "ramo", header: "Ramo", render: (item) => (
            <span className="text-sm text-slate-700">{item.ramo || "No especificado"}</span>
          )},
          { key: "alicuota", header: "Alícuota", render: (item) => (
            <span className="font-mono text-blue-600">{item.alicuota}%</span>
          )},
          { key: "minimo_tributable", header: "Mínimo Trib.", render: (item) => (
            <span className="font-mono">{item.minimo_tributable?.toFixed(2)}</span>
          )}
        ];
      case "configuracion-general":
        return [
          { key: "nombre_alcaldia", header: "Alcaldía", render: (item) => (
            <span className="font-semibold text-blue-700">{item.nombre_alcaldia || "No configurado"}</span>
          )},
          { key: "municipio", header: "Municipio" },
          { key: "estado", header: "Estado" },
          { key: "nombre_alcalde", header: "Alcalde/Alcaldesa" },
          { key: "moneda_calculo", header: "Moneda", render: (item) => (
            <Badge variant="outline" className="bg-green-50 text-green-700">{item.moneda_calculo || "Bolivares"}</Badge>
          )},
          { key: "validacion_carga_documentos", header: "Valid. Docs", render: (item) => (
            <Badge variant="outline" className={item.validacion_carga_documentos ? "bg-green-50 text-green-700" : "bg-slate-50 text-slate-600"}>
              {item.validacion_carga_documentos ? "Sí" : "No"}
            </Badge>
          )}
        ];
      default:
        return [];
    }
  };

  // If no submodulo selected, show landing page
  if (!submodulo || !currentConfig) {
    return (
      <div className="space-y-6" data-testid="definiciones-page">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Definiciones
            </h1>
            <p className="text-slate-500">Seleccione un submódulo del menú lateral</p>
          </div>
        </div>

        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Configuración del Sistema</h3>
              <p className="text-slate-500 mb-6">
                Utilice el menú lateral para acceder a los diferentes submódulos de definiciones.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(submoduloConfig).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <Button
                      key={key}
                      variant="outline"
                      className="flex flex-col items-center gap-2 h-auto py-4"
                      onClick={() => navigate(`/definiciones/${key}`)}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs">{config.title}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const Icon = currentConfig.icon;
  const columns = getTableColumns();

  // Special view for Manuales
  if (submodulo === "manuales") {
    return (
      <div className="p-4 md:p-6">
        <ManualesPage API={API} token={token} />
      </div>
    );
  }

  // Special view for Flujogramas
  if (submodulo === "flujogramas") {
    return (
      <div className="p-4 md:p-6">
        <FlujogramasPage API={API} token={token} />
      </div>
    );
  }

  // Special view for Configuración - Form-based single record
  if (submodulo === "configuracion-general") {
    const configData = data[0] || {};

    // Update config form when data loads
    if (data[0] && Object.keys(configForm).length === 0) {
      setConfigForm(data[0]);
    }

    const handleSaveConfig = async () => {
      try {
        if (configData.id) {
          await axios.put(`${API}/definiciones/configuracion/${configData.id}`, configForm, { headers });
        } else {
          await axios.post(`${API}/definiciones/configuracion`, configForm, { headers });
        }
        toast.success("Configuración guardada exitosamente");
        setIsEditingConfig(false);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Error al guardar configuración");
      }
    };

    return (
      <div className="space-y-6" data-testid="definiciones-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Configuración del Sistema
              </h1>
              <p className="text-slate-500">Parámetros generales de la Alcaldía</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEditingConfig ? (
              <Button 
                className="bg-blue-800 hover:bg-blue-900 gap-2"
                onClick={() => {
                  setConfigForm(configData);
                  setIsEditingConfig(true);
                }}
                data-testid="btn-editar-config"
              >
                <Pencil className="w-4 h-4" />
                Editar Configuración
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setIsEditingConfig(false);
                    setConfigForm(configData);
                  }}
                >
                  Cancelar
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700 gap-2"
                  onClick={handleSaveConfig}
                  data-testid="btn-guardar-config"
                >
                  Guardar Cambios
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Configuration Form */}
        {/* Logos de Encabezado - Full width */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-purple-700">
              <ImageIcon className="w-5 h-5" />
              Logos de Encabezado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo Izquierdo */}
              <div className="space-y-3">
                <Label className="text-slate-600">Imagen Escudo</Label>
                {isEditingConfig ? (
                  <div className="space-y-2">
                    <Input 
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setConfigForm({...configForm, logo_encabezado_izquierdo: reader.result});
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="cursor-pointer"
                      data-testid="input-logo-izquierdo"
                    />
                    {configForm.logo_encabezado_izquierdo && (
                      <div className="relative inline-block">
                        <img 
                          src={configForm.logo_encabezado_izquierdo} 
                          alt="Logo Izquierdo" 
                          className="max-h-24 rounded border"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                          onClick={() => setConfigForm({...configForm, logo_encabezado_izquierdo: null})}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  configData.logo_encabezado_izquierdo ? (
                    <img 
                      src={configData.logo_encabezado_izquierdo} 
                      alt="Logo Izquierdo" 
                      className="max-h-24 rounded border"
                    />
                  ) : (
                    <p className="text-slate-400 italic">Sin logo</p>
                  )
                )}
              </div>
              {/* Logo Derecho */}
              <div className="space-y-3">
                <Label className="text-slate-600">Logo Institucional</Label>
                {isEditingConfig ? (
                  <div className="space-y-2">
                    <Input 
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setConfigForm({...configForm, logo_encabezado_derecho: reader.result});
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="cursor-pointer"
                      data-testid="input-logo-derecho"
                    />
                    {configForm.logo_encabezado_derecho && (
                      <div className="relative inline-block">
                        <img 
                          src={configForm.logo_encabezado_derecho} 
                          alt="Logo Derecho" 
                          className="max-h-24 rounded border"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                          onClick={() => setConfigForm({...configForm, logo_encabezado_derecho: null})}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  configData.logo_encabezado_derecho ? (
                    <img 
                      src={configData.logo_encabezado_derecho} 
                      alt="Logo Derecho" 
                      className="max-h-24 rounded border"
                    />
                  ) : (
                    <p className="text-slate-400 italic">Sin logo</p>
                  )
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Datos de la Alcaldía */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                <Building2 className="w-5 h-5" />
                Datos de la Alcaldía
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Estado</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.estado || ""} 
                      onChange={(e) => setConfigForm({...configForm, estado: e.target.value})}
                      placeholder="Ej: Miranda"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.estado || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Municipio</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.municipio || ""} 
                      onChange={(e) => setConfigForm({...configForm, municipio: e.target.value})}
                      placeholder="Ej: Zamora"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.municipio || "-"}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Nombre de la Alcaldía</Label>
                {isEditingConfig ? (
                  <Input 
                    value={configForm.nombre_alcaldia || ""} 
                    onChange={(e) => setConfigForm({...configForm, nombre_alcaldia: e.target.value})}
                    placeholder="Ej: Alcaldía del Municipio Zamora"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{configData.nombre_alcaldia || "-"}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">RIF Alcaldía</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.rif_alcaldia || ""} 
                      onChange={(e) => setConfigForm({...configForm, rif_alcaldia: e.target.value})}
                      placeholder="Ej: G-20000001-0"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.rif_alcaldia || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Alcalde/Alcaldesa</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.nombre_alcalde || ""} 
                      onChange={(e) => setConfigForm({...configForm, nombre_alcalde: e.target.value})}
                      placeholder="Nombre completo"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.nombre_alcalde || "-"}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Dirección Fiscal</Label>
                {isEditingConfig ? (
                  <Input 
                    value={configForm.direccion_fiscal || ""} 
                    onChange={(e) => setConfigForm({...configForm, direccion_fiscal: e.target.value})}
                    placeholder="Dirección completa"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{configData.direccion_fiscal || "-"}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Teléfono Fijo</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.telefono_fijo || ""} 
                      onChange={(e) => setConfigForm({...configForm, telefono_fijo: e.target.value})}
                      placeholder="Ej: 0212-1234567"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.telefono_fijo || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Teléfono Móvil</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.telefono_movil || ""} 
                      onChange={(e) => setConfigForm({...configForm, telefono_movil: e.target.value})}
                      placeholder="Ej: 0414-1234567"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.telefono_movil || "-"}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dirección de Tributos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                <DollarSign className="w-5 h-5" />
                Dirección de Tributos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-600">Nombre Superintendencia/Dirección</Label>
                {isEditingConfig ? (
                  <Input 
                    value={configForm.nombre_superintendencia_tributos || ""} 
                    onChange={(e) => setConfigForm({...configForm, nombre_superintendencia_tributos: e.target.value})}
                    placeholder="Ej: SUMAT"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{configData.nombre_superintendencia_tributos || "-"}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Director(a) de Tributos</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.nombre_director_tributos || ""} 
                      onChange={(e) => setConfigForm({...configForm, nombre_director_tributos: e.target.value})}
                      placeholder="Nombre completo"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.nombre_director_tributos || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Email Tributos</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="email"
                      value={configForm.email_tributos || ""} 
                      onChange={(e) => setConfigForm({...configForm, email_tributos: e.target.value})}
                      placeholder="tributos@alcaldia.gob.ve"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.email_tributos || "-"}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Firma Director(a) Tributos</Label>
                {isEditingConfig ? (
                  <Input 
                    value={configForm.firma_director_tributos || ""} 
                    onChange={(e) => setConfigForm({...configForm, firma_director_tributos: e.target.value})}
                    placeholder="Texto para pie de página"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{configData.firma_director_tributos || "-"}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Catastro */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-yellow-700">
                <FileText className="w-5 h-5" />
                Catastro
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Director(a) de Catastro</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.nombre_director_catastro || ""} 
                      onChange={(e) => setConfigForm({...configForm, nombre_director_catastro: e.target.value})}
                      placeholder="Nombre completo"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.nombre_director_catastro || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Email Catastro</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="email"
                      value={configForm.email_catastro || ""} 
                      onChange={(e) => setConfigForm({...configForm, email_catastro: e.target.value})}
                      placeholder="catastro@alcaldia.gob.ve"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.email_catastro || "-"}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Firma Director(a) Catastro</Label>
                {isEditingConfig ? (
                  <Input 
                    value={configForm.firma_director_catastro || ""} 
                    onChange={(e) => setConfigForm({...configForm, firma_director_catastro: e.target.value})}
                    placeholder="Texto para pie de página"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{configData.firma_director_catastro || "-"}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Ingeniería Municipal */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-purple-700">
                <Building2 className="w-5 h-5" />
                Ingeniería Municipal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Director(a) de Ingeniería</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.nombre_director_ingenieria || ""} 
                      onChange={(e) => setConfigForm({...configForm, nombre_director_ingenieria: e.target.value})}
                      placeholder="Nombre completo"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.nombre_director_ingenieria || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Email Ingeniería</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="email"
                      value={configForm.email_ingenieria || ""} 
                      onChange={(e) => setConfigForm({...configForm, email_ingenieria: e.target.value})}
                      placeholder="ingenieria@alcaldia.gob.ve"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.email_ingenieria || "-"}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Firma Director(a) Ingeniería</Label>
                {isEditingConfig ? (
                  <Input 
                    value={configForm.firma_director_ingenieria || ""} 
                    onChange={(e) => setConfigForm({...configForm, firma_director_ingenieria: e.target.value})}
                    placeholder="Texto para pie de página"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{configData.firma_director_ingenieria || "-"}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Cementerio */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-stone-700">
                <FileText className="w-5 h-5" />
                Cementerio Municipal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Director(a) del Cementerio</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.nombre_director_cementerio || ""} 
                      onChange={(e) => setConfigForm({...configForm, nombre_director_cementerio: e.target.value})}
                      placeholder="Nombre completo"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.nombre_director_cementerio || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Email Cementerio</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="email"
                      value={configForm.email_cementerio || ""} 
                      onChange={(e) => setConfigForm({...configForm, email_cementerio: e.target.value})}
                      placeholder="cementerio@alcaldia.gob.ve"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.email_cementerio || "-"}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Firma Director(a) Cementerio</Label>
                {isEditingConfig ? (
                  <Input 
                    value={configForm.firma_director_cementerio || ""} 
                    onChange={(e) => setConfigForm({...configForm, firma_director_cementerio: e.target.value})}
                    placeholder="Texto para pie de página"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{configData.firma_director_cementerio || "-"}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Publicidad */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                <Megaphone className="w-5 h-5" />
                Publicidad
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-600">Director(a) de Publicidad</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.nombre_director_publicidad || ""} 
                      onChange={(e) => setConfigForm({...configForm, nombre_director_publicidad: e.target.value})}
                      placeholder="Nombre completo"
                      data-testid="form-director-publicidad"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.nombre_director_publicidad || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Email Publicidad</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="email"
                      value={configForm.email_publicidad || ""} 
                      onChange={(e) => setConfigForm({...configForm, email_publicidad: e.target.value})}
                      placeholder="publicidad@alcaldia.gob.ve"
                      data-testid="form-email-publicidad"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.email_publicidad || "-"}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Firma Director(a) Publicidad</Label>
                {isEditingConfig ? (
                  <Input 
                    value={configForm.firma_director_publicidad || ""} 
                    onChange={(e) => setConfigForm({...configForm, firma_director_publicidad: e.target.value})}
                    placeholder="Texto para pie de página"
                    data-testid="form-firma-publicidad"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{configData.firma_director_publicidad || "-"}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Vehículos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-emerald-700">
                <Car className="w-5 h-5" />
                Vehículos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-600">Director(a) de Vehículos</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.nombre_director_vehiculos || ""} 
                      onChange={(e) => setConfigForm({...configForm, nombre_director_vehiculos: e.target.value})}
                      placeholder="Nombre completo"
                      data-testid="form-director-vehiculos"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.nombre_director_vehiculos || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Email Vehículos</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="email"
                      value={configForm.email_vehiculos || ""} 
                      onChange={(e) => setConfigForm({...configForm, email_vehiculos: e.target.value})}
                      placeholder="vehiculos@alcaldia.gob.ve"
                      data-testid="form-email-vehiculos"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.email_vehiculos || "-"}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Firma Director(a) Vehículos</Label>
                {isEditingConfig ? (
                  <Input 
                    value={configForm.firma_director_vehiculos || ""} 
                    onChange={(e) => setConfigForm({...configForm, firma_director_vehiculos: e.target.value})}
                    placeholder="Texto para pie de página"
                    data-testid="form-firma-vehiculos"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{configData.firma_director_vehiculos || "-"}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Vendedores Ambulantes */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-teal-700">
                <ShoppingBag className="w-5 h-5" />
                Vendedores Ambulantes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-600">Director(a) de Vendedores Ambulantes</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.nombre_director_ambulantes || ""} 
                      onChange={(e) => setConfigForm({...configForm, nombre_director_ambulantes: e.target.value})}
                      placeholder="Nombre completo"
                      data-testid="form-director-ambulantes"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.nombre_director_ambulantes || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Email Vendedores Ambulantes</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="email"
                      value={configForm.email_ambulantes || ""} 
                      onChange={(e) => setConfigForm({...configForm, email_ambulantes: e.target.value})}
                      placeholder="ambulantes@alcaldia.gob.ve"
                      data-testid="form-email-ambulantes"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.email_ambulantes || "-"}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Firma Director(a) Vendedores Ambulantes</Label>
                {isEditingConfig ? (
                  <Input 
                    value={configForm.firma_director_ambulantes || ""} 
                    onChange={(e) => setConfigForm({...configForm, firma_director_ambulantes: e.target.value})}
                    placeholder="Texto para pie de página"
                    data-testid="form-firma-ambulantes"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{configData.firma_director_ambulantes || "-"}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Aseo Urbano */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-lime-700">
                <Truck className="w-5 h-5" />
                Aseo Urbano
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-600">Director(a) de Aseo Urbano</Label>
                  {isEditingConfig ? (
                    <Input 
                      value={configForm.nombre_director_aseo_urbano || ""} 
                      onChange={(e) => setConfigForm({...configForm, nombre_director_aseo_urbano: e.target.value})}
                      placeholder="Nombre completo"
                      data-testid="form-director-aseo"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.nombre_director_aseo_urbano || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Email Aseo Urbano</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="email"
                      value={configForm.email_aseo_urbano || ""} 
                      onChange={(e) => setConfigForm({...configForm, email_aseo_urbano: e.target.value})}
                      placeholder="aseourbano@alcaldia.gob.ve"
                      data-testid="form-email-aseo"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.email_aseo_urbano || "-"}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Firma Director(a) Aseo Urbano</Label>
                {isEditingConfig ? (
                  <Input 
                    value={configForm.firma_director_aseo_urbano || ""} 
                    onChange={(e) => setConfigForm({...configForm, firma_director_aseo_urbano: e.target.value})}
                    placeholder="Texto para pie de página"
                    data-testid="form-firma-aseo"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{configData.firma_director_aseo_urbano || "-"}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Parámetros de Declaración */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                <Calculator className="w-5 h-5" />
                Parámetros de Declaración
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Fecha Tope sin Multa</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="date"
                      value={configForm.fecha_tope_sin_multa || ""} 
                      onChange={(e) => setConfigForm({...configForm, fecha_tope_sin_multa: e.target.value})}
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.fecha_tope_sin_multa || "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Fecha Tope con Descuento</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="date"
                      value={configForm.fecha_tope_con_descuento || ""} 
                      onChange={(e) => setConfigForm({...configForm, fecha_tope_con_descuento: e.target.value})}
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.fecha_tope_con_descuento || "-"}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Valor Multa (%)</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="number"
                      step="0.01"
                      value={configForm.valor_multa || ""} 
                      onChange={(e) => setConfigForm({...configForm, valor_multa: parseFloat(e.target.value) || 0})}
                      placeholder="Ej: 10"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.valor_multa ? `${configData.valor_multa}%` : "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Fecha Multa</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="date"
                      value={configForm.fecha_multa || ""} 
                      onChange={(e) => setConfigForm({...configForm, fecha_multa: e.target.value})}
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.fecha_multa || "-"}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Descuento Act. Económica (%)</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="number"
                      step="0.01"
                      value={configForm.valor_descuento_actividad_economica || ""} 
                      onChange={(e) => setConfigForm({...configForm, valor_descuento_actividad_economica: parseFloat(e.target.value) || 0})}
                      placeholder="Ej: 5"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.valor_descuento_actividad_economica ? `${configData.valor_descuento_actividad_economica}%` : "-"}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Fecha Inicio del Descuento</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="date"
                      value={configForm.fecha_inicio_descuento || ""} 
                      onChange={(e) => setConfigForm({...configForm, fecha_inicio_descuento: e.target.value})}
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{configData.fecha_inicio_descuento || "-"}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Moneda de Cálculo</Label>
                {isEditingConfig ? (
                  <Select 
                    value={configForm.moneda_calculo || "Bolivares"} 
                    onValueChange={(value) => setConfigForm({...configForm, moneda_calculo: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione moneda" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bolivares">Bolívares</SelectItem>
                      <SelectItem value="Dolares">Dólares</SelectItem>
                      <SelectItem value="Euros">Euros</SelectItem>
                      <SelectItem value="Petros">Petros</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline" className="bg-green-50 text-green-700">{configData.moneda_calculo || "Bolivares"}</Badge>
                )}
              </div>

              {/* Toggle Descuento por Solvencias */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between p-4 rounded-lg border-2 transition-colors" 
                  style={{ borderColor: (isEditingConfig ? configForm.descuento_solvencias : configData.descuento_solvencias) ? "#22c55e" : "#e2e8f0", backgroundColor: (isEditingConfig ? configForm.descuento_solvencias : configData.descuento_solvencias) ? "#f0fdf4" : "#fafafa" }}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-green-600" />
                      <Label className="text-base font-semibold text-slate-800 cursor-pointer">Descuento por Solvencias</Label>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-7">
                      {(isEditingConfig ? configForm.descuento_solvencias : configData.descuento_solvencias)
                        ? "Activado: Solo contribuyentes solventes en Solvencia Inmobiliaria y Conformidad de Uso pueden gozar del descuento."
                        : "Desactivado: Todos los contribuyentes con LAE pueden gozar del descuento sin validar solvencias."
                      }
                    </p>
                  </div>
                  <div className="ml-4">
                    {isEditingConfig ? (
                      <button
                        type="button"
                        onClick={() => setConfigForm({...configForm, descuento_solvencias: !configForm.descuento_solvencias})}
                        className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${configForm.descuento_solvencias ? "bg-green-500" : "bg-slate-300"}`}
                        data-testid="toggle-descuento-solvencias"
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${configForm.descuento_solvencias ? "translate-x-8" : "translate-x-1"}`} />
                      </button>
                    ) : (
                      <Badge className={`text-sm px-3 py-1 ${configData.descuento_solvencias ? "bg-green-100 text-green-700 border-green-300" : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                        {configData.descuento_solvencias ? "Activado" : "Desactivado"}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Toggle Bloquear Descuento por Mínimo Tributable */}
              <div className="pt-1">
                <div className="flex items-center justify-between p-4 rounded-lg border-2 transition-colors"
                  style={{ borderColor: (isEditingConfig ? configForm.bloquear_descuento_minimo_tributable : configData.bloquear_descuento_minimo_tributable) ? "#dc2626" : "#e2e8f0", backgroundColor: (isEditingConfig ? configForm.bloquear_descuento_minimo_tributable : configData.bloquear_descuento_minimo_tributable) ? "#fef2f2" : "#fafafa" }}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-red-600" />
                      <Label className="text-base font-semibold text-slate-800 cursor-pointer">Bloquear Descuento por Mínimo Tributable</Label>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-7">
                      {(isEditingConfig ? configForm.bloquear_descuento_minimo_tributable : configData.bloquear_descuento_minimo_tributable)
                        ? "Activado: Los contribuyentes con LAE que declaran mínimo tributable NO podrán gozar del descuento en Actividades Económicas."
                        : "Desactivado: Los contribuyentes con LAE que declaran mínimo tributable SÍ podrán gozar del descuento en Actividades Económicas."
                      }
                    </p>
                  </div>
                  <div className="ml-4">
                    {isEditingConfig ? (
                      <button
                        type="button"
                        onClick={() => setConfigForm({...configForm, bloquear_descuento_minimo_tributable: !configForm.bloquear_descuento_minimo_tributable})}
                        className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${configForm.bloquear_descuento_minimo_tributable ? "bg-red-500" : "bg-slate-300"}`}
                        data-testid="toggle-bloquear-descuento-minimo"
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${configForm.bloquear_descuento_minimo_tributable ? "translate-x-8" : "translate-x-1"}`} />
                      </button>
                    ) : (
                      <Badge className={`text-sm px-3 py-1 ${configData.bloquear_descuento_minimo_tributable ? "bg-red-100 text-red-700 border-red-300" : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                        {configData.bloquear_descuento_minimo_tributable ? "Activado" : "Desactivado"}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Correlativos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-cyan-700">
                <CreditCard className="w-5 h-5" />
                Correlativos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Comprobantes Pago</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="number"
                      value={configForm.correlativo_comprobantes_pago || 1} 
                      onChange={(e) => setConfigForm({...configForm, correlativo_comprobantes_pago: parseInt(e.target.value) || 1})}
                    />
                  ) : (
                    <p className="font-medium text-slate-900 text-lg">{configData.correlativo_comprobantes_pago || 1}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">LAE</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="number"
                      value={configForm.correlativo_lae || 1} 
                      onChange={(e) => setConfigForm({...configForm, correlativo_lae: parseInt(e.target.value) || 1})}
                    />
                  ) : (
                    <p className="font-medium text-slate-900 text-lg">{configData.correlativo_lae || 1}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Planilla Liquidación</Label>
                  {isEditingConfig ? (
                    <Input 
                      type="number"
                      value={configForm.correlativo_planilla_liquidacion || 1} 
                      onChange={(e) => setConfigForm({...configForm, correlativo_planilla_liquidacion: parseInt(e.target.value) || 1})}
                    />
                  ) : (
                    <p className="font-medium text-slate-900 text-lg">{configData.correlativo_planilla_liquidacion || 1}</p>
                  )}
                </div>
              </div>
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-600">Validación Carga de Documentos</Label>
                  {isEditingConfig ? (
                    <Switch 
                      checked={configForm.validacion_carga_documentos || false}
                      onCheckedChange={(checked) => setConfigForm({...configForm, validacion_carga_documentos: checked})}
                    />
                  ) : (
                    <Badge variant="outline" className={configData.validacion_carga_documentos ? "bg-green-50 text-green-700" : "bg-slate-50 text-slate-600"}>
                      {configData.validacion_carga_documentos ? "Activo" : "Inactivo"}
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Acciones Masivas por Módulo */}
        <AccionesMasivasPanel API={API} headers={headers} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="definiciones-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 bg-${currentConfig.color}-100 rounded-xl flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${currentConfig.color}-600`} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {currentConfig.title}
            </h1>
            <p className="text-slate-500">Gestión de {currentConfig.title.toLowerCase()}</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchData} 
          disabled={loading}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Content Card */}
      <Card>
        <CardContent className="p-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 flex-1 max-w-sm">
              {submodulo !== "actividades" && (
                <>
                  <Search className="w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-9"
                    data-testid="search-input"
                  />
                </>
              )}
              {submodulo === "actividades" && (
                <span className="text-sm text-slate-500">{filteredData.length} de {data.length} registros</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Export Button - All submodules */}
              {submodulo && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2" data-testid="btn-exportar">
                      <Download className="w-4 h-4" />
                      Exportar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={exportToHTML} className="gap-2 cursor-pointer">
                      <FileType className="w-4 h-4" />
                      HTML
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer">
                      <FileText className="w-4 h-4" />
                      CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToText} className="gap-2 cursor-pointer">
                      <File className="w-4 h-4" />
                      Texto
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer">
                      <FileSpreadsheet className="w-4 h-4" />
                      Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer">
                      <FileText className="w-4 h-4" />
                      PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToJSON} className="gap-2 cursor-pointer">
                      <FileJson className="w-4 h-4" />
                      JSON
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {/* Botón Limpiar Filtros - Solo para Tipos de Impuestos */}
              {submodulo === "impuestos" && (
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => setImpuestosFilters({
                    nombre: "",
                    tasa: "",
                    tipo_calculo: "",
                    codigo_presupuestario: "",
                    oficina: "",
                    extranet: "",
                    descuento: "",
                    estado: ""
                  })}
                  data-testid="btn-limpiar-filtros"
                >
                  <X className="w-4 h-4" />
                  Limpiar Filtros
                </Button>
              )}
              {/* Botón Limpiar Filtros - Actividades Económicas */}
              {submodulo === "actividades" && Object.values(actividadesFilters).some(v => v !== "") && (
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => setActividadesFilters({
                    codigo: "",
                    nombre: "",
                    codigo_presupuestario: "",
                    sector: "",
                    ramo: "",
                    alicuota: "",
                    minimo: ""
                  })}
                  data-testid="btn-limpiar-filtros-actividades"
                >
                  <X className="w-4 h-4" />
                  Limpiar Filtros
                </Button>
              )}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-blue-800 hover:bg-blue-900 gap-2"
                    onClick={openCreateDialog}
                    data-testid="btn-nuevo"
                  >
                    <Plus className="w-4 h-4" />
                    Nuevo
                  </Button>
                </DialogTrigger>
                <DialogContent className={submodulo === "configuracion-general" ? "sm:max-w-3xl" : "sm:max-w-md"}>
                  <DialogHeader>
                    <DialogTitle>
                      {editingItem ? `Editar ${currentConfig.title}` : (submodulo === "configuracion-general" ? "Configuración de la Alcaldía" : `Nuevo ${currentConfig.title}`)}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    {renderFormFields()}
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancelar</Button>
                    </DialogClose>
                    <Button 
                      className="bg-blue-800 hover:bg-blue-900"
                      onClick={editingItem ? handleUpdate : handleCreate}
                      data-testid="btn-guardar"
                    >
                      {editingItem ? "Actualizar" : "Guardar"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {columns.map((col, idx) => (
                    <th key={idx} className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      {col.header}
                    </th>
                  ))}
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
                {/* Fila de filtros para Tipos de Impuestos */}
                {submodulo === "impuestos" && (
                  <tr className="border-b border-slate-200 bg-white">
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={impuestosFilters.nombre}
                        onChange={(e) => setImpuestosFilters({...impuestosFilters, nombre: e.target.value})}
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={impuestosFilters.tasa}
                        onChange={(e) => setImpuestosFilters({...impuestosFilters, tasa: e.target.value})}
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={impuestosFilters.tipo_calculo}
                        onChange={(e) => setImpuestosFilters({...impuestosFilters, tipo_calculo: e.target.value})}
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={impuestosFilters.codigo_presupuestario}
                        onChange={(e) => setImpuestosFilters({...impuestosFilters, codigo_presupuestario: e.target.value})}
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={impuestosFilters.oficina}
                        onChange={(e) => setImpuestosFilters({...impuestosFilters, oficina: e.target.value})}
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={impuestosFilters.extranet}
                        onChange={(e) => setImpuestosFilters({...impuestosFilters, extranet: e.target.value})}
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={impuestosFilters.descuento}
                        onChange={(e) => setImpuestosFilters({...impuestosFilters, descuento: e.target.value})}
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={impuestosFilters.estado}
                        onChange={(e) => setImpuestosFilters({...impuestosFilters, estado: e.target.value})}
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="py-2 px-4"></td>
                  </tr>
                )}
                {/* Fila de filtros para Actividades Económicas */}
                {submodulo === "actividades" && (
                  <tr className="border-b border-slate-200 bg-white">
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={actividadesFilters.codigo}
                        onChange={(e) => setActividadesFilters({...actividadesFilters, codigo: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="actividades-filter-codigo"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={actividadesFilters.nombre}
                        onChange={(e) => setActividadesFilters({...actividadesFilters, nombre: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="actividades-filter-nombre"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={actividadesFilters.codigo_presupuestario}
                        onChange={(e) => setActividadesFilters({...actividadesFilters, codigo_presupuestario: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="actividades-filter-codigo-presupuestario"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={actividadesFilters.sector}
                        onChange={(e) => setActividadesFilters({...actividadesFilters, sector: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="actividades-filter-sector"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={actividadesFilters.ramo}
                        onChange={(e) => setActividadesFilters({...actividadesFilters, ramo: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="actividades-filter-ramo"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={actividadesFilters.alicuota}
                        onChange={(e) => setActividadesFilters({...actividadesFilters, alicuota: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="actividades-filter-alicuota"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <Input
                        placeholder="Filtrar..."
                        value={actividadesFilters.minimo}
                        onChange={(e) => setActividadesFilters({...actividadesFilters, minimo: e.target.value})}
                        className="h-8 text-xs"
                        data-testid="actividades-filter-minimo"
                      />
                    </td>
                    <td className="py-2 px-4"></td>
                  </tr>
                )}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="py-8 text-center">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Cargando...
                      </div>
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="py-8 text-center text-slate-500">
                      No hay registros. Haga clic en "Nuevo" para agregar.
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, idx) => (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`row-${idx}`}>
                      {columns.map((col, colIdx) => (
                        <td key={colIdx} className="py-3 px-4 text-sm text-slate-700">
                          {col.render ? col.render(item) : item[col.key]}
                        </td>
                      ))}
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => openEditDialog(item)}
                            className="h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            data-testid={`edit-btn-${idx}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleDelete(item.id)}
                            className="h-8 w-8 text-red-600 hover:text-red-800 hover:bg-red-50"
                            data-testid={`delete-btn-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Diálogo de confirmación para cotización */}
      <AlertDialog open={confirmCotizacionDialog} onOpenChange={setConfirmCotizacionDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar cambio de cotización activa</AlertDialogTitle>
            <AlertDialogDescription>
              Ya existe una cotización en estatus "Activo". Al guardar este registro, 
              la cotización anterior pasará automáticamente a estatus "Inactivo".
              <br /><br />
              ¿Desea continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelCotizacion}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCotizacion}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DefinicionesPage;
